/**
 * NestedDataGrid.js — Generic, reusable nested/expandable grid component.
 *
 * Usage:
 *   import { NestedDataGrid, ExpandableNestedCell, buildColumnTree } from './components/NestedDataGrid';
 *
 * Props (NestedDataGrid):
 *   columns   — array of column objects (from buildColumnTree output)
 *   data      — array of row objects from the API
 *   compact   — bool: true for sub-grid rendering (smaller padding/font)
 *   pageSize  — number of rows per page (default 10, 0 = show all)
 *
 * Column object shape:
 *   { resultColumnId, columnHeader, apiFieldName, columnWidth, childColumns: [] }
 *
 * buildColumnTree(flatColumns):
 *   Converts a flat array with parentColumnId into a nested tree.
 *   Top-level columns  → parentColumnId == null
 *   Child columns      → parentColumnId == <parent resultColumnId>
 */

import React, { useState, useCallback } from "react";
import { Badge, Spinner } from "react-bootstrap";

const API_URL = process.env.REACT_APP_API_URL || "";

// ─────────────────────────────────────────────────────────────────────────────
// Helper: build column tree from the flat API list
// ─────────────────────────────────────────────────────────────────────────────
export function buildColumnTree(flatColumns) {
  const topLevel = flatColumns.filter((c) => !c.parentColumnId);
  const childMap  = {};

  flatColumns
    .filter((c) => c.parentColumnId != null)
    .forEach((c) => {
      if (!childMap[c.parentColumnId]) childMap[c.parentColumnId] = [];
      childMap[c.parentColumnId].push(c);
    });

  function attachChildren(col) {
    const children = (childMap[col.resultColumnId] || []).map(attachChildren);
    return { ...col, childColumns: children };
  }

  return topLevel.map(attachChildren);
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: read a dot-path from an object  e.g. "address.city"
// ─────────────────────────────────────────────────────────────────────────────
function getNestedValue(obj, path) {
  return path.split(".").reduce((cur, key) => (cur != null ? cur[key] : undefined), obj);
}

// ─────────────────────────────────────────────────────────────────────────────
// EditableCheckbox — toggleable checkbox that calls an API on change
// ─────────────────────────────────────────────────────────────────────────────
function EditableCheckbox({ col, value, row, onDataChange, onError }) {
  const [checked, setChecked] = useState(!!value);
  const [saving, setSaving] = useState(false);

  const handleToggle = useCallback(async () => {
    if (saving || !col.editApiUrl) return;

    const newValue = !checked;
    setSaving(true);

    try {
      // Resolve placeholders in editApiUrl from row data
      // e.g. "/api/Territory/{territoryID}/salesrep/{salesRepCode}/primary"
      const resolvedUrl = col.editApiUrl.replace(/\{(\w+)\}/g, (match, key) => {
        const found = Object.keys(row).find(
          (k) => k.toLowerCase() === key.toLowerCase()
        );
        return found ? encodeURIComponent(row[found]) : match;
      });

      const response = await fetch(`${API_URL}${resolvedUrl}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [col.apiFieldName]: newValue }),
      });

      if (response.ok) {
        setChecked(newValue);
        // Notify parent to update the data
        if (onDataChange) onDataChange(row, col.apiFieldName, newValue);
      } else {
        const body = await response.text();
        let msg = body || `Server error: ${response.status}`;
        try {
          const j = JSON.parse(body);
          msg = j.message || j.error || j.title || body;
        } catch { /* not JSON */ }
        console.error("Failed to update:", msg);
        if (onError) onError(msg);
      }
    } catch (err) {
      console.error("Error updating:", err);
      if (onError) onError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  }, [checked, saving, col, row, onDataChange, onError]);

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
      {saving ? (
        <Spinner animation="border" size="sm" style={{ width: 16, height: 16 }} />
      ) : (
        <>
          <input
            type="checkbox"
            checked={checked}
            onChange={() => {}} // controlled — actual toggle in onClick
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleToggle();
            }}
            style={{
              width: 16,
              height: 16,
              cursor: "pointer",
              accentColor: "#0052A5",
            }}
            title={checked ? "Click to remove primary" : "Click to set as primary"}
          />
          <Badge bg={checked ? "primary" : "secondary"} pill style={{ fontSize: "10px" }}>
            {checked ? "Primary" : "Alt"}
          </Badge>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Renders a single scalar cell value (no arrays)
// ─────────────────────────────────────────────────────────────────────────────
export function ScalarCell({ col, value }) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted">—</span>;
  }

  if (typeof value === "boolean") {
    // Primary / Alt
    if (col.apiFieldName === "primary" || col.apiFieldName === "isPrimary") {
      return value
        ? <Badge bg="primary" pill>Primary</Badge>
        : <Badge bg="secondary" pill>Alt</Badge>;
    }
    // Active / Inactive — compact circle badge
    if (col.apiFieldName.toLowerCase() === "inactive") {
      const isInactive = value;
      return (
        <span
          title={isInactive ? "Inactive" : "Active"}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: isInactive ? "#dc3545" : "#198754",
            color: "#fff",
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {isInactive ? "I" : "A"}
        </span>
      );
    }
    // Generic boolean
    return value
      ? <span className="text-success fw-semibold">✓</span>
      : <span className="text-muted">✗</span>;
  }

  return <span>{String(value)}</span>;
}

// ─────────────────────────────────────────────────────────────────────────────
// ExpandableNestedCell — shown inside a parent grid cell when the value is
// an array and child columns are configured.
// Exported so callers can use it directly in their own table cells.
// ─────────────────────────────────────────────────────────────────────────────
export function ExpandableNestedCell({ columns, data, parentLabel, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!data || data.length === 0) {
    return <span className="text-muted fst-italic" style={{ fontSize: "12px" }}>None</span>;
  }

  const label = `${data.length} ${parentLabel || "item"}${data.length !== 1 ? "s" : ""}`;

  return (
    <div>
      <button
        className="btn btn-link btn-sm p-0 text-decoration-none"
        style={{ fontSize: "12px", color: "#0052A5", fontWeight: 500 }}
        onClick={() => setExpanded((p) => !p)}
        title={expanded ? "Collapse" : "Expand"}
      >
        <span style={{ marginRight: "5px", fontSize: "10px" }}>
          {expanded ? "▼" : "▶"}
        </span>
        {label}
      </button>

      {expanded && (
        <div
          style={{
            marginTop: "6px",
            border: "1px solid #c8d8e8",
            borderRadius: "4px",
            overflow: "hidden",
            boxShadow: "0 1px 4px rgba(0,82,165,0.08)",
          }}
        >
          <NestedDataGrid columns={columns} data={data} compact />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// NestedDataGrid — the recursive table renderer
// Works at any depth: top-level grid, nested grids, deeply nested grids
// ─────────────────────────────────────────────────────────────────────────────
export function NestedDataGrid({ columns, data, compact = false, pageSize = 10, onDataChange, onRowClick, onError, readOnly = false }) {
  const [currentPage, setCurrentPage] = useState(0);

  if (!columns || columns.length === 0) return null;

  const fontSize  = compact ? "11px" : "13px";
  const cellPad   = compact ? "4px 8px" : "8px 12px";
  const headBg    = compact ? "#dce8f5" : "#0052A5";
  const headColor = compact ? "#0052A5" : "#ffffff";
  const headPad   = compact ? "5px 8px" : "8px 12px";

  if (!data || data.length === 0) {
    return (
      <div style={{ padding: cellPad, fontSize, color: "#888", fontStyle: "italic" }}>
        No records
      </div>
    );
  }

  // Pagination logic
  const usePagination = pageSize > 0 && data.length > pageSize;
  const totalPages    = usePagination ? Math.ceil(data.length / pageSize) : 1;
  const pageData      = usePagination
    ? data.slice(currentPage * pageSize, (currentPage + 1) * pageSize)
    : data;
  const showFrom      = usePagination ? currentPage * pageSize + 1 : 1;
  const showTo        = usePagination ? Math.min((currentPage + 1) * pageSize, data.length) : data.length;

  return (
    <div>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize,
          tableLayout: "auto",
        }}
      >
        <thead>
          <tr>
            {onRowClick && (
              <th
                style={{
                  background: headBg,
                  color: headColor,
                  padding: headPad,
                  fontWeight: 600,
                  border: "1px solid #c8d8e8",
                  whiteSpace: "nowrap",
                  width: "32px",
                  textAlign: "center",
                }}
              />
            )}
            {columns.map((col) => (
              <th
                key={col.resultColumnId || col.apiFieldName}
                style={{
                  background: headBg,
                  color: headColor,
                  padding: headPad,
                  fontWeight: 600,
                  border: "1px solid #c8d8e8",
                  whiteSpace: "nowrap",
                  width: col.columnWidth || "auto",
                }}
              >
                {col.columnHeader}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pageData.map((row, ri) => (
            <tr
              key={ri}
              style={{ background: ri % 2 === 0 ? "#ffffff" : "#f4f8fc" }}
            >
              {onRowClick && (
                <td
                  style={{
                    padding: cellPad,
                    border: "1px solid #dee2e6",
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: "32px",
                  }}
                >
                  <span
                    onClick={(e) => { e.stopPropagation(); onRowClick(row); }}
                    title="Edit"
                    style={{
                      cursor: "pointer",
                      color: "#0052A5",
                      fontSize: compact ? "12px" : "14px",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = "#003d7a"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = "#0052A5"; }}
                  >
                    ✏️
                  </span>
                </td>
              )}
              {columns.map((col) => {
                const value = getNestedValue(row, col.apiFieldName);

                // Array field with configured child columns → nested expandable grid
                if (Array.isArray(value) && col.childColumns?.length > 0) {
                  return (
                    <td
                      key={col.resultColumnId || col.apiFieldName}
                      style={{
                        padding: cellPad,
                        border: "1px solid #dee2e6",
                        verticalAlign: "top",
                      }}
                    >
                      <ExpandableNestedCell
                        columns={col.childColumns}
                        data={value}
                        parentLabel={col.columnHeader.toLowerCase()}
                      />
                    </td>
                  );
                }

                // Editable boolean column → checkbox (static badge when readOnly)
                if (!readOnly && col.isEditable && col.editApiUrl && typeof value === "boolean") {
                  return (
                    <td
                      key={col.resultColumnId || col.apiFieldName}
                      style={{
                        padding: cellPad,
                        border: "1px solid #dee2e6",
                        verticalAlign: "middle",
                        textAlign: "center",
                      }}
                    >
                      <EditableCheckbox col={col} value={value} row={row} onDataChange={onDataChange} onError={onError} />
                    </td>
                  );
                }

                // Scalar cell
                return (
                  <td
                    key={col.resultColumnId || col.apiFieldName}
                    style={{
                      padding: cellPad,
                      border: "1px solid #dee2e6",
                      verticalAlign: "middle",
                    }}
                  >
                    <ScalarCell col={col} value={value} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination controls */}
      {usePagination && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: compact ? "4px 8px" : "6px 12px",
            background: "#f8f9fa",
            borderTop: "1px solid #dee2e6",
            fontSize: compact ? "11px" : "12px",
            color: "#666",
          }}
        >
          <span>
            {showFrom}–{showTo} of {data.length}
          </span>
          <div style={{ display: "flex", gap: "4px" }}>
            <button
              type="button"
              disabled={currentPage === 0}
              onClick={() => setCurrentPage(0)}
              style={paginationBtnStyle(compact, currentPage === 0)}
              title="First"
            >
              «
            </button>
            <button
              type="button"
              disabled={currentPage === 0}
              onClick={() => setCurrentPage((p) => p - 1)}
              style={paginationBtnStyle(compact, currentPage === 0)}
              title="Previous"
            >
              ‹
            </button>
            <span style={{ padding: "2px 8px", fontWeight: 500 }}>
              {currentPage + 1} / {totalPages}
            </span>
            <button
              type="button"
              disabled={currentPage >= totalPages - 1}
              onClick={() => setCurrentPage((p) => p + 1)}
              style={paginationBtnStyle(compact, currentPage >= totalPages - 1)}
              title="Next"
            >
              ›
            </button>
            <button
              type="button"
              disabled={currentPage >= totalPages - 1}
              onClick={() => setCurrentPage(totalPages - 1)}
              style={paginationBtnStyle(compact, currentPage >= totalPages - 1)}
              title="Last"
            >
              »
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Small helper for pagination button styles
function paginationBtnStyle(compact, disabled) {
  return {
    border: "1px solid #ccc",
    background: disabled ? "#eee" : "#fff",
    color: disabled ? "#aaa" : "#0052A5",
    cursor: disabled ? "default" : "pointer",
    borderRadius: "3px",
    padding: compact ? "1px 6px" : "2px 8px",
    fontSize: compact ? "11px" : "12px",
    fontWeight: 600,
    lineHeight: 1.4,
  };
}

export default NestedDataGrid;
